import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Plus, Edit2, Trash2, Calendar, Users, Shirt, Lock, Eye, EyeOff, X, Award, Star, Upload, Image, MessageSquare, Mail, Quote, HelpCircle, ClipboardList, TrendingUp, Bell, CheckCircle, Clock, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { adminApi, getStoredAdminPassword, clearStoredAdminPassword } from "@/lib/adminApi";
import { setStoredParentSession } from "@/lib/parentApi";
import { useConfirm } from "@/lib/useConfirm";
import AdminLoginForm from "@/components/AdminLoginForm";
import ConfirmDialog from "@/components/ConfirmDialog";
import { AdminEmailTool } from "@/components/AdminEmailTool";
import CampPhotoManager from "@/components/CampPhotoManager";
import ParentPortalStats from "@/components/ParentPortalStats";

const LOGO_URL = "/images/ym6gev0b_AE8ECFCA-9BBA-44D9-A534-DED3210EC5A4.png";

const AGE_GROUPS = ["U9", "U10", "U11", "U12", "U13", "U14", "U15", "U9-U11", "U12-U15", "U9-U15", "All Ages"];

export default function AdminDashboard() {
  const [isAuthenticated, setIsAuthenticated] = useState(() => !!getStoredAdminPassword());
  const [activeTab, setActiveTab] = useState("camps");
  const { confirmState, requestConfirm, setOpen: setConfirmOpen } = useConfirm();
  
  const [camps, setCamps] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [preorders, setPreorders] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [contactMessages, setContactMessages] = useState([]);
  const [newsletterSubscribers, setNewsletterSubscribers] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [pendingTestimonials, setPendingTestimonials] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isCreateCoachDialogOpen, setIsCreateCoachDialogOpen] = useState(false);
  const [isEditCoachDialogOpen, setIsEditCoachDialogOpen] = useState(false);
  const [isCreateTestimonialDialogOpen, setIsCreateTestimonialDialogOpen] = useState(false);
  const [isEditTestimonialDialogOpen, setIsEditTestimonialDialogOpen] = useState(false);
  const [isCreateFaqDialogOpen, setIsCreateFaqDialogOpen] = useState(false);
  const [isEditFaqDialogOpen, setIsEditFaqDialogOpen] = useState(false);
  const [editingCamp, setEditingCamp] = useState(null);
  const [editingCoach, setEditingCoach] = useState(null);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [editingFaq, setEditingFaq] = useState(null);
  
  const [campForm, setCampForm] = useState({
    name: "",
    name_fr: "",
    description: "",
    description_fr: "",
    start_date: "",
    end_date: "",
    time: "",
    location: "Miramichi, New Brunswick",
    age_group: "",
    max_participants: 30
  });

  const [coachForm, setCoachForm] = useState({
    name: "",
    title: "",
    title_fr: "",
    bio: "",
    bio_fr: "",
    experience_years: 1,
    specialties: [],
    specialties_fr: [],
    photo_url: "",
    is_founder: false,
    display_order: 0
  });

  const [testimonialForm, setTestimonialForm] = useState({
    name: "",
    role: "",
    quote: "",
    rating: 5,
    photo_url: ""
  });

  const [faqForm, setFaqForm] = useState({
    question: "",
    answer: "",
    question_fr: "",
    answer_fr: "",
    display_order: 0
  });

  const [specialtyInput, setSpecialtyInput] = useState("");
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const fileInputRef = useRef(null);

  const handleLogout = () => {
    clearStoredAdminPassword();
    setIsAuthenticated(false);
    toast.success("Logged out");
  };

  // Open the Parent Portal as a real parent so admins can preview the
  // experience. Defaults to the seeded test account (chris@thechrisburns.com).
  const handlePreviewParentPortal = async (email = "chris@thechrisburns.com") => {
    try {
      const { data } = await adminApi.post("/admin/parent-impersonate", { email });
      setStoredParentSession(data.token, data.email);
      window.open("/parent/dashboard", "_blank", "noopener,noreferrer");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to open parent portal preview");
    }
  };

  // If we already have a stored password, fetch data on mount
  useEffect(() => {
    if (isAuthenticated) {
      fetchAllData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAllData = async () => {
    setIsLoading(true);
    try {
      const [campsRes, regsRes, preordersRes, coachesRes, contactRes, newsletterRes, testimonialsRes, faqsRes, waitlistRes, pendingRes] = await Promise.all([
        adminApi.get(`/admin/camps`),
        adminApi.get(`/admin/registrations`),
        adminApi.get(`/admin/tshirt-preorders`),
        adminApi.get(`/admin/coaches`),
        adminApi.get(`/admin/contact-messages`),
        adminApi.get(`/admin/newsletter-subscribers`),
        adminApi.get(`/admin/testimonials`),
        adminApi.get(`/admin/faqs`),
        adminApi.get(`/admin/waitlist`),
        adminApi.get(`/admin/pending-testimonials`)
      ]);
      setCamps(campsRes.data);
      setRegistrations(regsRes.data);
      setPreorders(preordersRes.data);
      setCoaches(coachesRes.data);
      setContactMessages(contactRes.data);
      setNewsletterSubscribers(newsletterRes.data);
      setTestimonials(testimonialsRes.data);
      setFaqs(faqsRes.data);
      setWaitlist(waitlistRes.data);
      setPendingTestimonials(pendingRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load data");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateCamp = async (e) => {
    e.preventDefault();
    try {
      await adminApi.post(`/admin/camps`, campForm);
      toast.success("Camp created successfully!");
      setIsCreateDialogOpen(false);
      resetCampForm();
      fetchAllData();
    } catch (error) {
      console.error("Error creating camp:", error);
      toast.error("Failed to create camp");
    }
  };

  const handleUpdateCamp = async (e) => {
    e.preventDefault();
    if (!editingCamp) return;
    try {
      await adminApi.put(`/admin/camps/${editingCamp.id}`, campForm);
      toast.success("Camp updated successfully!");
      setIsEditDialogOpen(false);
      setEditingCamp(null);
      resetCampForm();
      fetchAllData();
    } catch (error) {
      console.error("Error updating camp:", error);
      toast.error("Failed to update camp");
    }
  };

  const handleDeleteCamp = (campId) => {
    requestConfirm({
      title: "Delete this camp?",
      description: "This will remove the camp and cannot be undone. Existing registrations are preserved.",
      confirmLabel: "Delete Camp",
      onConfirm: async () => {
        try {
          const response = await adminApi.delete(`/admin/camps/${campId}`);
          if (response.data.success) {
            toast.success("Camp deleted");
            fetchAllData();
          }
        } catch (error) {
          toast.error("Failed to delete camp");
        }
      },
    });
  };

  const handleToggleCampStatus = async (camp) => {
    try {
      await adminApi.put(`/admin/camps/${camp.id}`, { is_active: !camp.is_active });
      toast.success(`Camp ${camp.is_active ? "deactivated" : "activated"}`);
      fetchAllData();
    } catch (error) {
      console.error("Error toggling camp status:", error);
      toast.error("Failed to update camp status");
    }
  };

  const openEditDialog = (camp) => {
    setEditingCamp(camp);
    setCampForm({
      name: camp.name,
      name_fr: camp.name_fr || "",
      description: camp.description,
      description_fr: camp.description_fr || "",
      start_date: camp.start_date,
      end_date: camp.end_date,
      time: camp.time,
      location: camp.location,
      age_group: camp.age_group,
      max_participants: camp.max_participants
    });
    setIsEditDialogOpen(true);
  };

  const resetCampForm = () => {
    setCampForm({
      name: "",
      name_fr: "",
      description: "",
      description_fr: "",
      start_date: "",
      end_date: "",
      time: "",
      location: "Miramichi, New Brunswick",
      age_group: "",
      max_participants: 30
    });
  };

  // Coach functions
  const handleCreateCoach = async (e) => {
    e.preventDefault();
    try {
      await adminApi.post(`/admin/coaches`, coachForm);
      toast.success("Coach created successfully!");
      setIsCreateCoachDialogOpen(false);
      resetCoachForm();
      fetchAllData();
    } catch (error) {
      console.error("Error creating coach:", error);
      toast.error("Failed to create coach");
    }
  };

  const handleUpdateCoach = async (e) => {
    e.preventDefault();
    if (!editingCoach) return;
    try {
      await adminApi.put(`/admin/coaches/${editingCoach.id}`, coachForm);
      toast.success("Coach updated successfully!");
      setIsEditCoachDialogOpen(false);
      setEditingCoach(null);
      resetCoachForm();
      fetchAllData();
    } catch (error) {
      console.error("Error updating coach:", error);
      toast.error("Failed to update coach");
    }
  };

  const handleDeleteCoach = (coachId) => {
    requestConfirm({
      title: "Delete this coach?",
      description: "Their bio and photo will be removed from the public site.",
      confirmLabel: "Delete Coach",
      onConfirm: async () => {
        try {
          await adminApi.delete(`/admin/coaches/${coachId}`);
          toast.success("Coach deleted");
          fetchAllData();
        } catch (error) {
          toast.error("Failed to delete coach");
        }
      },
    });
  };

  const handleToggleCoachStatus = async (coach) => {
    try {
      await adminApi.put(`/admin/coaches/${coach.id}`, { is_active: !coach.is_active });
      toast.success(`Coach ${coach.is_active ? "deactivated" : "activated"}`);
      fetchAllData();
    } catch (error) {
      console.error("Error toggling coach status:", error);
      toast.error("Failed to update coach status");
    }
  };

  const openEditCoachDialog = (coach) => {
    setEditingCoach(coach);
    setCoachForm({
      name: coach.name,
      title: coach.title,
      title_fr: coach.title_fr || "",
      bio: coach.bio,
      bio_fr: coach.bio_fr || "",
      experience_years: coach.experience_years,
      specialties: coach.specialties || [],
      specialties_fr: coach.specialties_fr || [],
      photo_url: coach.photo_url || "",
      is_founder: coach.is_founder,
      display_order: coach.display_order
    });
    setIsEditCoachDialogOpen(true);
  };

  const resetCoachForm = () => {
    setCoachForm({
      name: "",
      title: "",
      title_fr: "",
      bio: "",
      bio_fr: "",
      experience_years: 1,
      specialties: [],
      specialties_fr: [],
      photo_url: "",
      is_founder: false,
      display_order: 0
    });
    setSpecialtyInput("");
  };

  const addSpecialty = () => {
    if (specialtyInput.trim() && !coachForm.specialties.includes(specialtyInput.trim())) {
      setCoachForm(prev => ({
        ...prev,
        specialties: [...prev.specialties, specialtyInput.trim()]
      }));
      setSpecialtyInput("");
    }
  };

  const removeSpecialty = (specialty) => {
    setCoachForm(prev => ({
      ...prev,
      specialties: prev.specialties.filter(s => s !== specialty)
    }));
  };

  const handlePhotoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Please upload a JPEG, PNG, GIF, or WebP image");
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return;
    }
    
    setIsUploadingPhoto(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await adminApi.post(`/admin/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Cloudinary returns a full secure URL
      const photoUrl = response.data.url;
      setCoachForm(prev => ({ ...prev, photo_url: photoUrl }));
      toast.success("Photo uploaded successfully!");
    } catch (error) {
      console.error("Error uploading photo:", error);
      toast.error("Failed to upload photo");
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  // Testimonial functions
  const handleCreateTestimonial = async (e) => {
    e.preventDefault();
    try {
      await adminApi.post(`/admin/testimonials`, testimonialForm);
      toast.success("Testimonial created successfully!");
      setIsCreateTestimonialDialogOpen(false);
      resetTestimonialForm();
      fetchAllData();
    } catch (error) {
      console.error("Error creating testimonial:", error);
      toast.error("Failed to create testimonial");
    }
  };

  const handleUpdateTestimonial = async (e) => {
    e.preventDefault();
    if (!editingTestimonial) return;
    try {
      await adminApi.put(`/admin/testimonials/${editingTestimonial.id}`, testimonialForm);
      toast.success("Testimonial updated successfully!");
      setIsEditTestimonialDialogOpen(false);
      setEditingTestimonial(null);
      resetTestimonialForm();
      fetchAllData();
    } catch (error) {
      console.error("Error updating testimonial:", error);
      toast.error("Failed to update testimonial");
    }
  };

  const handleDeleteTestimonial = (testimonialId) => {
    requestConfirm({
      title: "Delete this testimonial?",
      description: "It will be removed from the public site.",
      confirmLabel: "Delete Testimonial",
      onConfirm: async () => {
        try {
          await adminApi.delete(`/admin/testimonials/${testimonialId}`);
          toast.success("Testimonial deleted");
          fetchAllData();
        } catch (error) {
          toast.error("Failed to delete testimonial");
        }
      },
    });
  };

  const openEditTestimonialDialog = (testimonial) => {
    setEditingTestimonial(testimonial);
    setTestimonialForm({
      name: testimonial.name,
      role: testimonial.role,
      quote: testimonial.quote,
      rating: testimonial.rating,
      photo_url: testimonial.photo_url || ""
    });
    setIsEditTestimonialDialogOpen(true);
  };

  const resetTestimonialForm = () => {
    setTestimonialForm({
      name: "",
      role: "",
      quote: "",
      rating: 5,
      photo_url: ""
    });
  };

  // FAQ functions
  const handleCreateFaq = async (e) => {
    e.preventDefault();
    try {
      await adminApi.post(`/admin/faqs`, faqForm);
      toast.success("FAQ created successfully!");
      setIsCreateFaqDialogOpen(false);
      resetFaqForm();
      fetchAllData();
    } catch (error) {
      console.error("Error creating FAQ:", error);
      toast.error("Failed to create FAQ");
    }
  };

  const handleUpdateFaq = async (e) => {
    e.preventDefault();
    if (!editingFaq) return;
    try {
      await adminApi.put(`/admin/faqs/${editingFaq.id}`, faqForm);
      toast.success("FAQ updated successfully!");
      setIsEditFaqDialogOpen(false);
      setEditingFaq(null);
      resetFaqForm();
      fetchAllData();
    } catch (error) {
      console.error("Error updating FAQ:", error);
      toast.error("Failed to update FAQ");
    }
  };

  const handleDeleteFaq = (faqId) => {
    requestConfirm({
      title: "Delete this FAQ?",
      description: "It will be removed from the public FAQs section.",
      confirmLabel: "Delete FAQ",
      onConfirm: async () => {
        try {
          await adminApi.delete(`/admin/faqs/${faqId}`);
          toast.success("FAQ deleted");
          fetchAllData();
        } catch (error) {
          toast.error("Failed to delete FAQ");
        }
      },
    });
  };

  const handleToggleFaqStatus = async (faq) => {
    try {
      await adminApi.put(`/admin/faqs/${faq.id}`, { is_active: !faq.is_active });
      toast.success(`FAQ ${faq.is_active ? "deactivated" : "activated"}`);
      fetchAllData();
    } catch (error) {
      console.error("Error toggling FAQ status:", error);
      toast.error("Failed to update FAQ status");
    }
  };

  const openEditFaqDialog = (faq) => {
    setEditingFaq(faq);
    setFaqForm({
      question: faq.question,
      answer: faq.answer,
      question_fr: faq.question_fr || "",
      answer_fr: faq.answer_fr || "",
      display_order: faq.display_order
    });
    setIsEditFaqDialogOpen(true);
  };

  const resetFaqForm = () => {
    setFaqForm({
      question: "",
      answer: "",
      question_fr: "",
      answer_fr: "",
      display_order: 0
    });
  };

  const handleRemoveFromWaitlist = (entryId) => {
    requestConfirm({
      title: "Remove this entry from the waitlist?",
      description: "The waitlisted player will no longer be auto-notified if a spot opens.",
      confirmLabel: "Remove from Waitlist",
      onConfirm: async () => {
        try {
          await adminApi.delete(`/admin/waitlist/${entryId}`);
          toast.success("Removed from waitlist");
          fetchAllData();
        } catch (error) {
          toast.error("Failed to remove from waitlist");
        }
      },
    });
  };

  const handleDeleteRegistration = (reg) => {
    requestConfirm({
      title: `Delete registration for ${reg.participant_name}?`,
      description: "This permanently removes the player's registration and any coach notes / progress reports tied to it.",
      confirmLabel: "Delete Registration",
      onConfirm: async () => {
        try {
          await adminApi.delete(`/admin/registrations/${reg.id}`);
          toast.success("Registration deleted");
          fetchAllData();
        } catch (e) {
          toast.error("Failed to delete registration");
        }
      },
    });
  };

  const handleDeletePreorder = (preorder) => {
    requestConfirm({
      title: `Delete pre-order for ${preorder.participant_name}?`,
      description: "This T-shirt pre-order will be removed from the list.",
      confirmLabel: "Delete Pre-order",
      onConfirm: async () => {
        try {
          await adminApi.delete(`/admin/tshirt-preorders/${preorder.id}`);
          toast.success("Pre-order deleted");
          fetchAllData();
        } catch (e) {
          toast.error("Failed to delete pre-order");
        }
      },
    });
  };

  const handleDeleteContactMessage = (message) => {
    requestConfirm({
      title: `Delete message from ${message.name}?`,
      description: "This contact form submission will be permanently removed.",
      confirmLabel: "Delete Message",
      onConfirm: async () => {
        try {
          await adminApi.delete(`/admin/contact-messages/${message.id}`);
          toast.success("Message deleted");
          fetchAllData();
        } catch (e) {
          toast.error("Failed to delete message");
        }
      },
    });
  };

  const handleDeleteSubscriber = (sub) => {
    requestConfirm({
      title: `Remove ${sub.email} from the newsletter list?`,
      description: "They will no longer receive newsletter sends.",
      confirmLabel: "Remove Subscriber",
      onConfirm: async () => {
        try {
          await adminApi.delete(`/admin/newsletter-subscribers/${sub.id}`);
          toast.success("Subscriber removed");
          fetchAllData();
        } catch (e) {
          toast.error("Failed to remove subscriber");
        }
      },
    });
  };

  const handleApproveTestimonial = async (testimonialId) => {
    try {
      await adminApi.put(`/admin/testimonials/${testimonialId}/approve`);
      toast.success("Testimonial approved and now visible on website!");
      fetchAllData();
    } catch (error) {
      console.error("Error approving testimonial:", error);
      toast.error("Failed to approve testimonial");
    }
  };

  const handleSendTestimonialRequests = () => {
    requestConfirm({
      title: "Send testimonial-request emails?",
      description:
        "This will email a one-time, single-use testimonial-request link to every parent whose child finished a camp recently and hasn't been asked yet. Each parent gets at most one request per camp. Are you sure?",
      confirmLabel: "Send Requests",
      destructive: false,
      onConfirm: async () => {
        try {
          const res = await adminApi.post(`/admin/send-testimonial-requests`);
          if (res.data.requests_sent > 0) {
            toast.success(`${res.data.requests_sent} testimonial request(s) sent`);
          } else {
            toast.info(res.data.message || "No new requests to send");
          }
        } catch (error) {
          toast.error("Failed to send testimonial requests");
        }
      },
    });
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <AdminLoginForm
        title="Admin Access"
        subtitle="Enter password to manage camps, coaches, registrations, and more."
        onSuccess={() => {
          setIsAuthenticated(true);
          fetchAllData();
        }}
      />
    );
  }

  // Camp Form Component
  // Camp Form - using render function to prevent re-render issues
  const renderCampForm = (onSubmit, submitLabel) => (
    <form onSubmit={onSubmit} className="space-y-4" key="camp-form">
      <div>
        <Label htmlFor="camp_name_field" className="text-zinc-400">Camp Name *</Label>
        <Input
          id="camp_name_field"
          value={campForm.name}
          onChange={(e) => setCampForm(prev => ({ ...prev, name: e.target.value }))}
          className="bg-transparent border-white/20 text-white"
          required
          data-testid="camp-name-input"
        />
      </div>
      <div>
        <Label htmlFor="camp_name_fr" className="text-zinc-400">Camp Name (French) - Nom du camp en français</Label>
        <Input
          id="camp_name_fr"
          value={campForm.name_fr}
          onChange={(e) => setCampForm(prev => ({ ...prev, name_fr: e.target.value }))}
          className="bg-transparent border-white/20 text-white"
          placeholder="ex: Camp de Frappe U12"
          data-testid="camp-name-fr-input"
        />
      </div>
      <div>
        <Label htmlFor="description" className="text-zinc-400">Description (English) *</Label>
        <Textarea
          id="description"
          value={campForm.description}
          onChange={(e) => setCampForm(prev => ({ ...prev, description: e.target.value }))}
          className="bg-transparent border-white/20 text-white min-h-[80px]"
          required
          data-testid="camp-description-input"
        />
      </div>
      <div>
        <Label htmlFor="description_fr" className="text-zinc-400">Description (French) - Description en français</Label>
        <Textarea
          id="description_fr"
          value={campForm.description_fr}
          onChange={(e) => setCampForm(prev => ({ ...prev, description_fr: e.target.value }))}
          className="bg-transparent border-white/20 text-white min-h-[80px]"
          placeholder="Décrivez le camp en français..."
          data-testid="camp-description-fr-input"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="start_date" className="text-zinc-400">Start Date *</Label>
          <Input
            id="start_date"
            type="date"
            value={campForm.start_date}
            onChange={(e) => setCampForm(prev => ({ ...prev, start_date: e.target.value }))}
            className="bg-transparent border-white/20 text-white"
            required
            data-testid="camp-start-date-input"
          />
        </div>
        <div>
          <Label htmlFor="end_date" className="text-zinc-400">End Date *</Label>
          <Input
            id="end_date"
            type="date"
            value={campForm.end_date}
            onChange={(e) => setCampForm(prev => ({ ...prev, end_date: e.target.value }))}
            className="bg-transparent border-white/20 text-white"
            required
            data-testid="camp-end-date-input"
          />
        </div>
      </div>
      <div>
        <Label htmlFor="time" className="text-zinc-400">Time *</Label>
        <Input
          id="time"
          value={campForm.time}
          onChange={(e) => setCampForm(prev => ({ ...prev, time: e.target.value }))}
          className="bg-transparent border-white/20 text-white"
          placeholder="e.g., 9:00 AM - 12:00 PM"
          required
          data-testid="camp-time-input"
        />
      </div>
      <div>
        <Label htmlFor="location" className="text-zinc-400">Location *</Label>
        <Input
          id="location"
          value={campForm.location}
          onChange={(e) => setCampForm(prev => ({ ...prev, location: e.target.value }))}
          className="bg-transparent border-white/20 text-white"
          required
          data-testid="camp-location-input"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="age_group" className="text-zinc-400">Age Group *</Label>
          <Select 
            value={campForm.age_group} 
            onValueChange={(v) => setCampForm(prev => ({ ...prev, age_group: v }))}
            data-testid="camp-age-group-select"
          >
            <SelectTrigger className="bg-transparent border-white/20 text-white">
              <SelectValue placeholder="Select age group..." />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-white/20">
              {AGE_GROUPS.map(group => (
                <SelectItem key={group} value={group} className="text-white focus:bg-burns-red">
                  {group}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="max_participants" className="text-zinc-400">Max Participants</Label>
          <Input
            id="max_participants"
            type="number"
            min="1"
            value={campForm.max_participants}
            onChange={(e) => setCampForm(prev => ({ ...prev, max_participants: parseInt(e.target.value) }))}
            className="bg-transparent border-white/20 text-white"
            data-testid="camp-max-participants-input"
          />
        </div>
      </div>
      <Button type="submit" className="btn-primary w-full rounded-sm" data-testid="camp-submit-btn">
        {submitLabel}
      </Button>
    </form>
  );

  // Coach Form Component - using key to prevent re-render issues
  const renderCoachForm = (onSubmit, submitLabel) => (
    <form onSubmit={onSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2" key="coach-form">
      <div>
        <Label htmlFor="coach_name" className="text-zinc-400">Name *</Label>
        <Input
          id="coach_name"
          value={coachForm.name}
          onChange={(e) => setCoachForm(prev => ({ ...prev, name: e.target.value }))}
          className="bg-transparent border-white/20 text-white"
          required
          data-testid="coach-name-input"
        />
      </div>
      <div>
        <Label htmlFor="coach_title" className="text-zinc-400">Title (English) *</Label>
        <Input
          id="coach_title"
          value={coachForm.title}
          onChange={(e) => setCoachForm(prev => ({ ...prev, title: e.target.value }))}
          className="bg-transparent border-white/20 text-white"
          placeholder="e.g., Pitching Coach, Head Coach"
          required
          data-testid="coach-title-input"
        />
      </div>
      <div>
        <Label htmlFor="coach_title_fr" className="text-zinc-400">Title (French) - Titre en français</Label>
        <Input
          id="coach_title_fr"
          value={coachForm.title_fr}
          onChange={(e) => setCoachForm(prev => ({ ...prev, title_fr: e.target.value }))}
          className="bg-transparent border-white/20 text-white"
          placeholder="ex: Entraîneur de Lancer, Entraîneur-Chef"
          data-testid="coach-title-fr-input"
        />
      </div>
      <div>
        <Label htmlFor="coach_bio" className="text-zinc-400">Bio (English) *</Label>
        <Textarea
          id="coach_bio"
          value={coachForm.bio}
          onChange={(e) => setCoachForm(prev => ({ ...prev, bio: e.target.value }))}
          className="bg-transparent border-white/20 text-white min-h-[100px]"
          placeholder="Write a brief bio about the coach..."
          required
          data-testid="coach-bio-input"
        />
      </div>
      <div>
        <Label htmlFor="coach_bio_fr" className="text-zinc-400">Bio (French) - Biographie en français</Label>
        <Textarea
          id="coach_bio_fr"
          value={coachForm.bio_fr}
          onChange={(e) => setCoachForm(prev => ({ ...prev, bio_fr: e.target.value }))}
          className="bg-transparent border-white/20 text-white min-h-[100px]"
          placeholder="Écrivez une brève biographie de l'entraîneur..."
          data-testid="coach-bio-fr-input"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="experience_years" className="text-zinc-400">Years of Experience *</Label>
          <Input
            id="experience_years"
            type="number"
            min="1"
            value={coachForm.experience_years}
            onChange={(e) => setCoachForm(prev => ({ ...prev, experience_years: parseInt(e.target.value) }))}
            className="bg-transparent border-white/20 text-white"
            required
            data-testid="coach-experience-input"
          />
        </div>
        <div>
          <Label htmlFor="display_order" className="text-zinc-400">Display Order</Label>
          <Input
            id="display_order"
            type="number"
            min="0"
            value={coachForm.display_order}
            onChange={(e) => setCoachForm(prev => ({ ...prev, display_order: parseInt(e.target.value) }))}
            className="bg-transparent border-white/20 text-white"
            data-testid="coach-order-input"
          />
        </div>
      </div>
      <div>
        <Label className="text-zinc-400">Photo</Label>
        <div className="mt-2 space-y-3">
          {/* Upload Button */}
          <div className="flex items-center gap-3">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handlePhotoUpload}
              accept="image/jpeg,image/png,image/gif,image/webp"
              className="hidden"
            />
            <Button 
              type="button" 
              onClick={() => fileInputRef.current?.click()}
              className="btn-secondary flex items-center gap-2"
              disabled={isUploadingPhoto}
            >
              <Upload className="w-4 h-4" />
              {isUploadingPhoto ? "Uploading..." : "Upload Photo"}
            </Button>
            <span className="text-zinc-500 text-sm">or</span>
          </div>
          
          {/* URL Input */}
          <Input
            id="photo_url"
            value={coachForm.photo_url}
            onChange={(e) => setCoachForm(prev => ({ ...prev, photo_url: e.target.value }))}
            className="bg-transparent border-white/20 text-white"
            placeholder="Enter photo URL..."
            data-testid="coach-photo-input"
          />
          
          {/* Preview */}
          {coachForm.photo_url && (
            <div className="flex items-center gap-3">
              <div className="w-20 h-20 rounded-sm overflow-hidden border border-white/20">
                <img 
                  src={coachForm.photo_url.startsWith('http') ? coachForm.photo_url : `${BACKEND_URL}${coachForm.photo_url}`} 
                  alt="Preview" 
                  className="w-full h-full object-cover" 
                />
              </div>
              <Button 
                type="button" 
                variant="ghost" 
                size="sm"
                onClick={() => setCoachForm(prev => ({ ...prev, photo_url: "" }))}
                className="text-red-500 hover:text-red-400"
              >
                Remove
              </Button>
            </div>
          )}
        </div>
      </div>
      <div>
        <Label className="text-zinc-400">Specialties</Label>
        <div className="flex gap-2 mt-1">
          <Input
            value={specialtyInput}
            onChange={(e) => setSpecialtyInput(e.target.value)}
            className="bg-transparent border-white/20 text-white"
            placeholder="Add a specialty..."
            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSpecialty())}
            data-testid="coach-specialty-input"
          />
          <Button type="button" onClick={addSpecialty} className="btn-secondary px-4">
            Add
          </Button>
        </div>
        <div className="flex flex-wrap gap-2 mt-2">
          {coachForm.specialties.map((specialty, idx) => (
            <span 
              key={idx} 
              className="bg-burns-dark text-burns-gold px-3 py-1 rounded-sm text-sm flex items-center gap-2"
            >
              {specialty}
              <button type="button" onClick={() => removeSpecialty(specialty)} className="text-zinc-500 hover:text-white">
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      </div>
      <div className="flex items-center gap-3">
        <Checkbox
          id="is_founder"
          checked={coachForm.is_founder}
          onCheckedChange={(checked) => setCoachForm(prev => ({ ...prev, is_founder: checked }))}
          data-testid="coach-founder-checkbox"
        />
        <Label htmlFor="is_founder" className="text-zinc-400 cursor-pointer">This is the Founder</Label>
      </div>
      <Button type="submit" className="btn-primary w-full rounded-sm" data-testid="coach-submit-btn">
        {submitLabel}
      </Button>
    </form>
  );


  return (
    <div className="min-h-screen bg-burns-black" data-testid="admin-dashboard">
      {/* Header */}
      <header className="nav-glass sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <ArrowLeft className="w-5 h-5 text-zinc-400" />
            <img src={LOGO_URL} alt="Burns Elite Baseball" className="h-12 w-auto" />
          </Link>
          <div className="flex items-center gap-4">
            <span className="text-burns-gold font-heading text-lg hidden sm:inline">ADMIN DASHBOARD</span>
            <button
              onClick={() => handlePreviewParentPortal()}
              className="text-burns-gold hover:text-white text-sm font-medium"
              data-testid="open-parent-portal"
              title="Open the parent portal as the seeded test parent (chris@thechrisburns.com) in a new tab"
            >
              Open Parent Portal
            </button>
            <button
              onClick={handleLogout}
              className="text-zinc-400 hover:text-white text-sm font-medium"
              data-testid="admin-logout"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-8">
          <div className="bg-zinc-950 border border-white/10 rounded-sm p-3">
            <div className="flex items-center gap-2">
              <Calendar className="w-6 h-6 text-burns-red" />
              <div>
                <p className="text-zinc-400 text-xs">Camps</p>
                <p className="font-heading text-xl text-white">{camps.filter(c => c.is_active).length}</p>
              </div>
            </div>
          </div>
          <div className="bg-zinc-950 border border-white/10 rounded-sm p-3">
            <div className="flex items-center gap-2">
              <Users className="w-6 h-6 text-burns-gold" />
              <div>
                <p className="text-zinc-400 text-xs">Registrations</p>
                <p className="font-heading text-xl text-white">{registrations.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-zinc-950 border border-white/10 rounded-sm p-3">
            <div className="flex items-center gap-2">
              <Shirt className="w-6 h-6 text-burns-red" />
              <div>
                <p className="text-zinc-400 text-xs">T-Shirts</p>
                <p className="font-heading text-xl text-white">{preorders.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-zinc-950 border border-white/10 rounded-sm p-3">
            <div className="flex items-center gap-2">
              <Award className="w-6 h-6 text-burns-gold" />
              <div>
                <p className="text-zinc-400 text-xs">Coaches</p>
                <p className="font-heading text-xl text-white">{coaches.filter(c => c.is_active).length}</p>
              </div>
            </div>
          </div>
          <div className="bg-zinc-950 border border-white/10 rounded-sm p-3">
            <div className="flex items-center gap-2">
              <Quote className="w-6 h-6 text-burns-red" />
              <div>
                <p className="text-zinc-400 text-xs">Testimonials</p>
                <p className="font-heading text-xl text-white">{testimonials.filter(t => t.is_active).length}</p>
              </div>
            </div>
          </div>
          <div className="bg-zinc-950 border border-white/10 rounded-sm p-3">
            <div className="flex items-center gap-2">
              <HelpCircle className="w-6 h-6 text-burns-gold" />
              <div>
                <p className="text-zinc-400 text-xs">FAQs</p>
                <p className="font-heading text-xl text-white">{faqs.filter(f => f.is_active).length}</p>
              </div>
            </div>
          </div>
          <div className="bg-zinc-950 border border-white/10 rounded-sm p-3">
            <div className="flex items-center gap-2">
              <MessageSquare className="w-6 h-6 text-burns-red" />
              <div>
                <p className="text-zinc-400 text-xs">Messages</p>
                <p className="font-heading text-xl text-white">{contactMessages.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-zinc-950 border border-white/10 rounded-sm p-3">
            <div className="flex items-center gap-2">
              <Mail className="w-6 h-6 text-burns-gold" />
              <div>
                <p className="text-zinc-400 text-xs">Subscribers</p>
                <p className="font-heading text-xl text-white">{newsletterSubscribers.length}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="flex flex-wrap gap-3 mb-8">
          <Link to="/progress">
            <Button className="btn-secondary text-sm" data-testid="player-progress-link">
              <TrendingUp className="w-4 h-4 mr-2" /> Player Progress Tracking
            </Button>
          </Link>
          <Link to="/calendar">
            <Button className="btn-secondary text-sm" data-testid="calendar-link">
              <Calendar className="w-4 h-4 mr-2" /> View Camp Calendar
            </Button>
          </Link>
          <Button
            className="btn-secondary text-sm"
            onClick={() =>
              requestConfirm({
                title: "Send 3-day camp reminders?",
                description:
                  "This will email a reminder to every parent whose child has a camp starting in exactly 3 days, but only if they haven't already been emailed for that camp. Already-reminded parents are skipped automatically. Are you sure?",
                confirmLabel: "Send Reminders",
                destructive: false,
                onConfirm: async () => {
                  try {
                    const res = await adminApi.post(`/admin/send-camp-reminders`);
                    toast.success(`${res.data.reminders_sent} reminder(s) sent`);
                  } catch (error) {
                    toast.error("Failed to send reminders");
                  }
                },
              })
            }
            data-testid="send-reminders-btn"
          >
            <Bell className="w-4 h-4 mr-2" /> Send 3-Day Reminders
          </Button>
          <Button 
            className="btn-secondary text-sm"
            onClick={handleSendTestimonialRequests}
            data-testid="send-testimonial-requests-btn"
          >
            <Quote className="w-4 h-4 mr-2" /> Request Reviews
          </Button>
          {pendingTestimonials.length > 0 && (
            <button
              type="button"
              onClick={() => {
                setActiveTab("testimonials");
                // Defer until the tab actually mounts before scrolling
                setTimeout(() => {
                  document.getElementById("pending-approval-section")?.scrollIntoView({ behavior: "smooth", block: "start" });
                }, 60);
              }}
              className="inline-flex items-center bg-green-500/20 text-green-400 hover:bg-green-500/30 hover:text-green-300 px-3 py-1 rounded-sm text-sm transition-colors"
              data-testid="pending-approval-button"
            >
              <Clock className="w-4 h-4 mr-2" /> {pendingTestimonials.length} pending approval
            </button>
          )}
          {waitlist.length > 0 && (
            <span className="inline-flex items-center bg-yellow-500/20 text-yellow-400 px-3 py-1 rounded-sm text-sm">
              <ClipboardList className="w-4 h-4 mr-2" /> {waitlist.length} on waitlist
            </span>
          )}
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-zinc-950 border border-white/10 flex-wrap h-auto p-1">
            <TabsTrigger value="camps" className="data-[state=active]:bg-burns-red data-[state=active]:text-white text-xs sm:text-sm" data-testid="camps-tab">
              CAMPS
            </TabsTrigger>
            <TabsTrigger value="coaches" className="data-[state=active]:bg-burns-red data-[state=active]:text-white text-xs sm:text-sm" data-testid="coaches-tab">
              COACHES
            </TabsTrigger>
            <TabsTrigger value="testimonials" className="data-[state=active]:bg-burns-red data-[state=active]:text-white text-xs sm:text-sm" data-testid="testimonials-tab">
              TESTIMONIALS
            </TabsTrigger>
            <TabsTrigger value="faqs" className="data-[state=active]:bg-burns-red data-[state=active]:text-white text-xs sm:text-sm" data-testid="faqs-tab">
              FAQs
            </TabsTrigger>
            <TabsTrigger value="registrations" className="data-[state=active]:bg-burns-red data-[state=active]:text-white text-xs sm:text-sm" data-testid="registrations-tab">
              REGISTRATIONS
            </TabsTrigger>
            <TabsTrigger value="preorders" className="data-[state=active]:bg-burns-red data-[state=active]:text-white text-xs sm:text-sm" data-testid="preorders-tab">
              T-SHIRTS
            </TabsTrigger>
            <TabsTrigger value="waitlist" className="data-[state=active]:bg-burns-red data-[state=active]:text-white text-xs sm:text-sm" data-testid="waitlist-tab">
              WAITLIST ({waitlist.length})
            </TabsTrigger>
            <TabsTrigger value="messages" className="data-[state=active]:bg-burns-red data-[state=active]:text-white text-xs sm:text-sm" data-testid="messages-tab">
              MESSAGES
            </TabsTrigger>
            <TabsTrigger value="newsletter" className="data-[state=active]:bg-burns-red data-[state=active]:text-white text-xs sm:text-sm" data-testid="newsletter-tab">
              NEWSLETTER
            </TabsTrigger>
            <TabsTrigger value="photos" className="data-[state=active]:bg-burns-red data-[state=active]:text-white text-xs sm:text-sm" data-testid="photos-tab">
              Photos
            </TabsTrigger>
            <TabsTrigger value="portal-stats" className="data-[state=active]:bg-burns-red data-[state=active]:text-white text-xs sm:text-sm" data-testid="portal-stats-tab">
              Portal Stats
            </TabsTrigger>
            <TabsTrigger value="email-tool" className="data-[state=active]:bg-burns-red data-[state=active]:text-white text-xs sm:text-sm" data-testid="email-tool-tab">
              EMAIL TOOL
            </TabsTrigger>
          </TabsList>

          {/* Camps Tab */}
          <TabsContent value="camps">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-2xl font-bold text-white">MANAGE CAMPS</h2>
              <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="btn-primary rounded-sm" data-testid="create-camp-btn">
                    <Plus className="w-4 h-4 mr-2" /> ADD CAMP
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-zinc-900 border-white/20 text-white max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="font-heading text-2xl text-burns-gold">CREATE NEW CAMP</DialogTitle>
                  </DialogHeader>
                  {renderCampForm(handleCreateCamp, "CREATE CAMP")}
                </DialogContent>
              </Dialog>
            </div>

            {isLoading ? (
              <p className="text-zinc-400">Loading camps...</p>
            ) : camps.length === 0 ? (
              <div className="text-center py-16 bg-zinc-950 border border-white/10 rounded-sm">
                <Calendar className="w-16 h-16 text-burns-gold mx-auto mb-4" />
                <p className="text-zinc-400 mb-4">No camps created yet</p>
                <Button className="btn-secondary" onClick={() => setIsCreateDialogOpen(true)}>
                  Create First Camp
                </Button>
              </div>
            ) : (
              <div className="bg-zinc-950 border border-white/10 rounded-sm overflow-hidden">
                <table className="w-full admin-table">
                  <thead>
                    <tr className="bg-burns-dark">
                      <th>Camp Name</th>
                      <th>Dates</th>
                      <th>Age Group</th>
                      <th>Participants</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {camps.map(camp => (
                      <tr key={camp.id} data-testid={`camp-row-${camp.id}`}>
                        <td className="font-medium text-white">{camp.name}</td>
                        <td className="text-zinc-400">{camp.start_date} - {camp.end_date}</td>
                        <td className="text-zinc-400">{camp.age_group}</td>
                        <td className="text-zinc-400">{camp.current_participants}/{camp.max_participants}</td>
                        <td>
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={camp.is_active}
                              onCheckedChange={() => handleToggleCampStatus(camp)}
                              data-testid={`camp-status-toggle-${camp.id}`}
                            />
                            <span className={camp.is_active ? "text-green-500" : "text-zinc-500"}>
                              {camp.is_active ? "Active" : "Inactive"}
                            </span>
                          </div>
                        </td>
                        <td>
                          <div className="flex items-center gap-2">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditDialog(camp)}
                              className="text-burns-gold hover:text-white"
                              data-testid={`edit-camp-${camp.id}`}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteCamp(camp.id)}
                              className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                              data-testid={`delete-camp-${camp.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Edit Dialog */}
            <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
              <DialogContent className="bg-zinc-900 border-white/20 text-white max-w-lg">
                <DialogHeader>
                  <DialogTitle className="font-heading text-2xl text-burns-gold">EDIT CAMP</DialogTitle>
                </DialogHeader>
                {renderCampForm(handleUpdateCamp, "UPDATE CAMP")}
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Coaches Tab */}
          <TabsContent value="coaches">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-2xl font-bold text-white">MANAGE COACHES</h2>
              <Dialog open={isCreateCoachDialogOpen} onOpenChange={setIsCreateCoachDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="btn-primary rounded-sm" data-testid="create-coach-btn">
                    <Plus className="w-4 h-4 mr-2" /> ADD COACH
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-zinc-900 border-white/20 text-white max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="font-heading text-2xl text-burns-gold">ADD NEW COACH</DialogTitle>
                  </DialogHeader>
                  {renderCoachForm(handleCreateCoach, "CREATE COACH")}
                </DialogContent>
              </Dialog>
            </div>

            {isLoading ? (
              <p className="text-zinc-400">Loading coaches...</p>
            ) : coaches.length === 0 ? (
              <div className="text-center py-16 bg-zinc-950 border border-white/10 rounded-sm">
                <Award className="w-16 h-16 text-burns-gold mx-auto mb-4" />
                <p className="text-zinc-400 mb-4">No coaches added yet</p>
                <Button className="btn-secondary" onClick={() => setIsCreateCoachDialogOpen(true)}>
                  Add First Coach
                </Button>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {coaches.map(coach => (
                  <div 
                    key={coach.id} 
                    className={`bg-zinc-950 border rounded-sm overflow-hidden ${coach.is_founder ? 'border-burns-gold' : 'border-white/10'}`}
                    data-testid={`coach-card-${coach.id}`}
                  >
                    {coach.photo_url && (
                      <div className="aspect-[4/3] overflow-hidden">
                        <img 
                          src={coach.photo_url.startsWith('http') ? coach.photo_url : `${BACKEND_URL}${coach.photo_url}`} 
                          alt={coach.name} 
                          className="w-full h-full object-cover" 
                        />
                      </div>
                    )}
                    <div className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          {coach.is_founder && <Star className="w-4 h-4 text-burns-gold" />}
                          <span className="text-burns-gold text-sm">{coach.experience_years} yrs exp</span>
                        </div>
                        <Switch
                          checked={coach.is_active}
                          onCheckedChange={() => handleToggleCoachStatus(coach)}
                          data-testid={`coach-status-toggle-${coach.id}`}
                        />
                      </div>
                      <h3 className="font-heading text-xl text-white mb-1">{coach.name}</h3>
                      <p className="text-burns-red text-sm mb-2">{coach.title}</p>
                      <p className="text-zinc-400 text-sm line-clamp-2 mb-3">{coach.bio}</p>
                      <div className="flex flex-wrap gap-1 mb-4">
                        {coach.specialties?.slice(0, 2).map((s, i) => (
                          <span key={i} className="bg-burns-dark text-zinc-500 px-2 py-1 rounded-sm text-xs">{s}</span>
                        ))}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditCoachDialog(coach)}
                          className="flex-1 text-burns-gold hover:text-white border border-burns-gold/30"
                          data-testid={`edit-coach-${coach.id}`}
                        >
                          <Edit2 className="w-4 h-4 mr-1" /> Edit
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteCoach(coach.id)}
                          className="text-red-500 hover:text-red-400 border border-red-500/30"
                          data-testid={`delete-coach-${coach.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Edit Coach Dialog */}
            <Dialog open={isEditCoachDialogOpen} onOpenChange={setIsEditCoachDialogOpen}>
              <DialogContent className="bg-zinc-900 border-white/20 text-white max-w-lg">
                <DialogHeader>
                  <DialogTitle className="font-heading text-2xl text-burns-gold">EDIT COACH</DialogTitle>
                </DialogHeader>
                {renderCoachForm(handleUpdateCoach, "UPDATE COACH")}
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Testimonials Tab */}
          <TabsContent value="testimonials">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-2xl font-bold text-white">MANAGE TESTIMONIALS</h2>
              <Dialog open={isCreateTestimonialDialogOpen} onOpenChange={setIsCreateTestimonialDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="btn-primary rounded-sm" data-testid="create-testimonial-btn">
                    <Plus className="w-4 h-4 mr-2" /> ADD TESTIMONIAL
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-zinc-900 border-white/20 text-white max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="font-heading text-2xl text-burns-gold">ADD TESTIMONIAL</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateTestimonial} className="space-y-4">
                    <div>
                      <Label className="text-zinc-400">Name *</Label>
                      <Input
                        value={testimonialForm.name}
                        onChange={(e) => setTestimonialForm(prev => ({ ...prev, name: e.target.value }))}
                        className="bg-transparent border-white/20 text-white"
                        placeholder="Parent's name"
                        required
                      />
                    </div>
                    <div>
                      <Label className="text-zinc-400">Role *</Label>
                      <Input
                        value={testimonialForm.role}
                        onChange={(e) => setTestimonialForm(prev => ({ ...prev, role: e.target.value }))}
                        className="bg-transparent border-white/20 text-white"
                        placeholder="e.g., Parent, Player U12, Coach"
                        required
                      />
                    </div>
                    <div>
                      <Label className="text-zinc-400">Quote *</Label>
                      <Textarea
                        value={testimonialForm.quote}
                        onChange={(e) => setTestimonialForm(prev => ({ ...prev, quote: e.target.value }))}
                        className="bg-transparent border-white/20 text-white min-h-[100px]"
                        placeholder="What they said about Burns Elite..."
                        required
                      />
                    </div>
                    <div>
                      <Label className="text-zinc-400">Rating (1-5)</Label>
                      <Select 
                        value={String(testimonialForm.rating)} 
                        onValueChange={(v) => setTestimonialForm(prev => ({ ...prev, rating: parseInt(v) }))}
                      >
                        <SelectTrigger className="bg-transparent border-white/20 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-white/20">
                          {[5, 4, 3, 2, 1].map(r => (
                            <SelectItem key={r} value={String(r)} className="text-white focus:bg-burns-red">
                              {r} Star{r !== 1 ? 's' : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button type="submit" className="btn-primary w-full rounded-sm">
                      CREATE TESTIMONIAL
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {isLoading ? (
              <p className="text-zinc-400">Loading testimonials...</p>
            ) : (
              <>
                {/* Pending Testimonials */}
                {pendingTestimonials.length > 0 && (
                  <div className="mb-8" id="pending-approval-section">
                    <h3 className="font-heading text-lg text-burns-gold mb-4 flex items-center gap-2">
                      <Clock className="w-5 h-5" /> PENDING APPROVAL ({pendingTestimonials.length})
                    </h3>
                    <div className="space-y-3">
                      {pendingTestimonials.map(testimonial => (
                        <div 
                          key={testimonial.id} 
                          className="bg-green-500/10 border border-green-500/30 rounded-sm p-4"
                          data-testid={`pending-testimonial-${testimonial.id}`}
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex gap-1 mb-2">
                                {[...Array(testimonial.rating)].map((_, i) => (
                                  <Star key={i} className="w-4 h-4 text-burns-gold fill-burns-gold" />
                                ))}
                              </div>
                              <p className="text-zinc-300 text-sm italic mb-2">"{testimonial.quote}"</p>
                              <p className="text-white font-medium text-sm">{testimonial.name}</p>
                              <p className="text-burns-gold text-xs">{testimonial.role}</p>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                onClick={() => handleApproveTestimonial(testimonial.id)}
                                className="btn-primary text-xs"
                              >
                                <CheckCircle className="w-4 h-4 mr-1" /> Approve
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteTestimonial(testimonial.id)}
                                className="text-red-500 hover:text-red-400"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Approved Testimonials */}
                {testimonials.length === 0 && pendingTestimonials.length === 0 ? (
                  <div className="text-center py-16 bg-zinc-950 border border-white/10 rounded-sm">
                    <Quote className="w-16 h-16 text-burns-gold mx-auto mb-4" />
                    <p className="text-zinc-400 mb-4">No testimonials yet</p>
                    <Button className="btn-secondary" onClick={() => setIsCreateTestimonialDialogOpen(true)}>
                      Add First Testimonial
                    </Button>
                  </div>
                ) : testimonials.length > 0 && (
                  <>
                    <h3 className="font-heading text-lg text-white mb-4">APPROVED TESTIMONIALS ({testimonials.length})</h3>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {testimonials.map(testimonial => (
                        <div 
                          key={testimonial.id} 
                          className={`bg-zinc-950 border rounded-sm p-4 ${testimonial.is_active ? 'border-white/10' : 'border-red-500/30 opacity-60'}`}
                          data-testid={`testimonial-card-${testimonial.id}`}
                        >
                          <div className="flex gap-1 mb-3">
                            {[...Array(testimonial.rating)].map((_, i) => (
                              <Star key={i} className="w-4 h-4 text-burns-gold fill-burns-gold" />
                            ))}
                          </div>
                          <p className="text-zinc-300 text-sm italic mb-3 line-clamp-3">"{testimonial.quote}"</p>
                          <div className="mb-3">
                            <p className="text-white font-medium">{testimonial.name}</p>
                            <p className="text-burns-gold text-xs">{testimonial.role}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditTestimonialDialog(testimonial)}
                              className="flex-1 text-burns-gold hover:text-white border border-burns-gold/30 text-xs"
                            >
                              <Edit2 className="w-3 h-3 mr-1" /> Edit
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteTestimonial(testimonial.id)}
                              className="text-red-500 hover:text-red-400 border border-red-500/30"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </>
            )}

            {/* Edit Testimonial Dialog */}
            <Dialog open={isEditTestimonialDialogOpen} onOpenChange={setIsEditTestimonialDialogOpen}>
              <DialogContent className="bg-zinc-900 border-white/20 text-white max-w-lg">
                <DialogHeader>
                  <DialogTitle className="font-heading text-2xl text-burns-gold">EDIT TESTIMONIAL</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleUpdateTestimonial} className="space-y-4">
                  <div>
                    <Label className="text-zinc-400">Name *</Label>
                    <Input
                      value={testimonialForm.name}
                      onChange={(e) => setTestimonialForm(prev => ({ ...prev, name: e.target.value }))}
                      className="bg-transparent border-white/20 text-white"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400">Role *</Label>
                    <Input
                      value={testimonialForm.role}
                      onChange={(e) => setTestimonialForm(prev => ({ ...prev, role: e.target.value }))}
                      className="bg-transparent border-white/20 text-white"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400">Quote *</Label>
                    <Textarea
                      value={testimonialForm.quote}
                      onChange={(e) => setTestimonialForm(prev => ({ ...prev, quote: e.target.value }))}
                      className="bg-transparent border-white/20 text-white min-h-[100px]"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400">Rating (1-5)</Label>
                    <Select 
                      value={String(testimonialForm.rating)} 
                      onValueChange={(v) => setTestimonialForm(prev => ({ ...prev, rating: parseInt(v) }))}
                    >
                      <SelectTrigger className="bg-transparent border-white/20 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-white/20">
                        {[5, 4, 3, 2, 1].map(r => (
                          <SelectItem key={r} value={String(r)} className="text-white focus:bg-burns-red">
                            {r} Star{r !== 1 ? 's' : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button type="submit" className="btn-primary w-full rounded-sm">
                    UPDATE TESTIMONIAL
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* FAQs Tab */}
          <TabsContent value="faqs">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-2xl font-bold text-white">MANAGE FAQs</h2>
              <Dialog open={isCreateFaqDialogOpen} onOpenChange={setIsCreateFaqDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="btn-primary rounded-sm" data-testid="create-faq-btn">
                    <Plus className="w-4 h-4 mr-2" /> ADD FAQ
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-zinc-900 border-white/20 text-white max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="font-heading text-2xl text-burns-gold">ADD FAQ</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateFaq} className="space-y-4">
                    <div>
                      <Label className="text-zinc-400">Question *</Label>
                      <Input
                        value={faqForm.question}
                        onChange={(e) => setFaqForm(prev => ({ ...prev, question: e.target.value }))}
                        className="bg-transparent border-white/20 text-white"
                        placeholder="What is the question?"
                        required
                      />
                    </div>
                    <div>
                      <Label className="text-zinc-400">Answer *</Label>
                      <Textarea
                        value={faqForm.answer}
                        onChange={(e) => setFaqForm(prev => ({ ...prev, answer: e.target.value }))}
                        className="bg-transparent border-white/20 text-white min-h-[120px]"
                        placeholder="Provide a detailed answer..."
                        required
                      />
                    </div>
                    <div>
                      <Label className="text-zinc-400">Question (French)</Label>
                      <Input
                        value={faqForm.question_fr}
                        onChange={(e) => setFaqForm(prev => ({ ...prev, question_fr: e.target.value }))}
                        className="bg-transparent border-white/20 text-white"
                        placeholder="Question en français..."
                      />
                    </div>
                    <div>
                      <Label className="text-zinc-400">Answer (French)</Label>
                      <Textarea
                        value={faqForm.answer_fr}
                        onChange={(e) => setFaqForm(prev => ({ ...prev, answer_fr: e.target.value }))}
                        className="bg-transparent border-white/20 text-white min-h-[100px]"
                        placeholder="Réponse en français..."
                      />
                    </div>
                    <div>
                      <Label className="text-zinc-400">Display Order</Label>
                      <Input
                        type="number"
                        min="0"
                        value={faqForm.display_order}
                        onChange={(e) => setFaqForm(prev => ({ ...prev, display_order: parseInt(e.target.value) || 0 }))}
                        className="bg-transparent border-white/20 text-white"
                      />
                    </div>
                    <Button type="submit" className="btn-primary w-full rounded-sm">
                      CREATE FAQ
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {isLoading ? (
              <p className="text-zinc-400">Loading FAQs...</p>
            ) : faqs.length === 0 ? (
              <div className="text-center py-16 bg-zinc-950 border border-white/10 rounded-sm">
                <HelpCircle className="w-16 h-16 text-burns-gold mx-auto mb-4" />
                <p className="text-zinc-400 mb-4">No FAQs yet</p>
                <Button className="btn-secondary" onClick={() => setIsCreateFaqDialogOpen(true)}>
                  Add First FAQ
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {faqs.map(faq => (
                  <div 
                    key={faq.id} 
                    className={`bg-zinc-950 border rounded-sm p-4 ${faq.is_active ? 'border-white/10' : 'border-red-500/30 opacity-60'}`}
                    data-testid={`faq-card-${faq.id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-burns-gold text-xs">#{faq.display_order}</span>
                          <h3 className="text-white font-medium">{faq.question}</h3>
                        </div>
                        <p className="text-zinc-400 text-sm line-clamp-2">{faq.answer}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={faq.is_active}
                          onCheckedChange={() => handleToggleFaqStatus(faq)}
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditFaqDialog(faq)}
                          className="text-burns-gold hover:text-white"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteFaq(faq.id)}
                          className="text-red-500 hover:text-red-400"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Edit FAQ Dialog */}
            <Dialog open={isEditFaqDialogOpen} onOpenChange={setIsEditFaqDialogOpen}>
              <DialogContent className="bg-zinc-900 border-white/20 text-white max-w-lg">
                <DialogHeader>
                  <DialogTitle className="font-heading text-2xl text-burns-gold">EDIT FAQ</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleUpdateFaq} className="space-y-4">
                  <div>
                    <Label className="text-zinc-400">Question *</Label>
                    <Input
                      value={faqForm.question}
                      onChange={(e) => setFaqForm(prev => ({ ...prev, question: e.target.value }))}
                      className="bg-transparent border-white/20 text-white"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400">Answer *</Label>
                    <Textarea
                      value={faqForm.answer}
                      onChange={(e) => setFaqForm(prev => ({ ...prev, answer: e.target.value }))}
                      className="bg-transparent border-white/20 text-white min-h-[120px]"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400">Question (French)</Label>
                    <Input
                      value={faqForm.question_fr}
                      onChange={(e) => setFaqForm(prev => ({ ...prev, question_fr: e.target.value }))}
                      className="bg-transparent border-white/20 text-white"
                      placeholder="Question en français..."
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400">Answer (French)</Label>
                    <Textarea
                      value={faqForm.answer_fr}
                      onChange={(e) => setFaqForm(prev => ({ ...prev, answer_fr: e.target.value }))}
                      className="bg-transparent border-white/20 text-white min-h-[100px]"
                      placeholder="Réponse en français..."
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400">Display Order</Label>
                    <Input
                      type="number"
                      min="0"
                      value={faqForm.display_order}
                      onChange={(e) => setFaqForm(prev => ({ ...prev, display_order: parseInt(e.target.value) || 0 }))}
                      className="bg-transparent border-white/20 text-white"
                    />
                  </div>
                  <Button type="submit" className="btn-primary w-full rounded-sm">
                    UPDATE FAQ
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Registrations Tab */}
          <TabsContent value="registrations">
            <h2 className="font-heading text-2xl font-bold text-white mb-6">CAMP REGISTRATIONS</h2>
            
            {isLoading ? (
              <p className="text-zinc-400">Loading registrations...</p>
            ) : registrations.length === 0 ? (
              <div className="text-center py-16 bg-zinc-950 border border-white/10 rounded-sm">
                <Users className="w-16 h-16 text-burns-gold mx-auto mb-4" />
                <p className="text-zinc-400">No registrations yet</p>
              </div>
            ) : (
              <div className="bg-zinc-950 border border-white/10 rounded-sm overflow-x-auto">
                <table className="w-full admin-table text-sm">
                  <thead>
                    <tr className="bg-burns-dark">
                      <th>Participant</th>
                      <th>Camp</th>
                      <th>Age/Gender</th>
                      <th>Skill Level</th>
                      <th>Parent</th>
                      <th>Contact</th>
                      <th>Emergency</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {registrations.map(reg => (
                      <tr key={reg.id} data-testid={`registration-row-${reg.id}`}>
                        <td className="font-medium text-white">{reg.participant_name}</td>
                        <td className="text-burns-gold">{reg.camp_name}</td>
                        <td className="text-zinc-400">{reg.age} / {reg.gender}</td>
                        <td className="text-zinc-400">{reg.skill_level}</td>
                        <td className="text-zinc-400">{reg.parent_name}</td>
                        <td className="text-zinc-400">
                          <div>{reg.parent_email}</div>
                          <div>{reg.parent_phone}</div>
                        </td>
                        <td className="text-zinc-400">
                          <div>{reg.emergency_contact_name}</div>
                          <div>{reg.emergency_contact_phone}</div>
                        </td>
                        <td>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteRegistration(reg)}
                            className="text-zinc-500 hover:text-burns-red"
                            data-testid={`delete-registration-${reg.id}`}
                            aria-label="Delete registration"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          {/* T-Shirt Pre-orders Tab */}
          <TabsContent value="preorders">
            <h2 className="font-heading text-2xl font-bold text-white mb-6">T-SHIRT PRE-ORDERS</h2>
            
            {isLoading ? (
              <p className="text-zinc-400">Loading pre-orders...</p>
            ) : preorders.length === 0 ? (
              <div className="text-center py-16 bg-zinc-950 border border-white/10 rounded-sm">
                <Shirt className="w-16 h-16 text-burns-gold mx-auto mb-4" />
                <p className="text-zinc-400">No pre-orders yet</p>
              </div>
            ) : (
              <>
                {/* Size Summary */}
                <div className="bg-zinc-950 border border-white/10 rounded-sm p-6 mb-6">
                  <h3 className="font-heading text-lg text-burns-gold mb-4">SIZE SUMMARY</h3>
                  <div className="flex flex-wrap gap-4">
                    {Object.entries(
                      preorders.reduce((acc, p) => {
                        acc[p.tshirt_size] = (acc[p.tshirt_size] || 0) + 1;
                        return acc;
                      }, {})
                    ).map(([size, count]) => (
                      <div key={size} className="bg-burns-dark px-4 py-2 rounded-sm">
                        <span className="text-white font-medium">{size}:</span>
                        <span className="text-burns-gold ml-2">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-zinc-950 border border-white/10 rounded-sm overflow-x-auto">
                  <table className="w-full admin-table text-sm">
                    <thead>
                      <tr className="bg-burns-dark">
                        <th>Participant</th>
                        <th>Age/Gender</th>
                        <th>T-Shirt Size</th>
                        <th>Parent</th>
                        <th>Contact</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {preorders.map(preorder => (
                        <tr key={preorder.id} data-testid={`preorder-row-${preorder.id}`}>
                          <td className="font-medium text-white">{preorder.participant_name}</td>
                          <td className="text-zinc-400">{preorder.age} / {preorder.gender}</td>
                          <td className="text-burns-gold font-medium">{preorder.tshirt_size}</td>
                          <td className="text-zinc-400">{preorder.parent_name}</td>
                          <td className="text-zinc-400">
                            <div>{preorder.parent_email}</div>
                            <div>{preorder.parent_phone}</div>
                          </td>
                          <td>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeletePreorder(preorder)}
                              className="text-zinc-500 hover:text-burns-red"
                              data-testid={`delete-preorder-${preorder.id}`}
                              aria-label="Delete pre-order"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </TabsContent>

          {/* Waitlist Tab */}
          <TabsContent value="waitlist">
            <h2 className="font-heading text-2xl font-bold text-white mb-6">CAMP WAITLIST</h2>
            
            {isLoading ? (
              <p className="text-zinc-400">Loading waitlist...</p>
            ) : waitlist.length === 0 ? (
              <div className="text-center py-16 bg-zinc-950 border border-white/10 rounded-sm">
                <ClipboardList className="w-16 h-16 text-burns-gold mx-auto mb-4" />
                <p className="text-zinc-400">No one on the waitlist</p>
                <p className="text-zinc-500 text-sm mt-2">When camps are full, users can join the waitlist from the calendar page.</p>
              </div>
            ) : (
              <div className="bg-zinc-950 border border-white/10 rounded-sm overflow-x-auto">
                <table className="w-full admin-table text-sm">
                  <thead>
                    <tr className="bg-burns-dark">
                      <th>Participant</th>
                      <th>Camp</th>
                      <th>Age</th>
                      <th>Parent</th>
                      <th>Contact</th>
                      <th>Date Added</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {waitlist.map(entry => (
                      <tr key={entry.id} data-testid={`waitlist-row-${entry.id}`}>
                        <td className="font-medium text-white">{entry.participant_name}</td>
                        <td className="text-burns-gold">{entry.camp_name}</td>
                        <td className="text-zinc-400">{entry.age}</td>
                        <td className="text-zinc-400">{entry.parent_name}</td>
                        <td className="text-zinc-400">
                          <div>{entry.parent_email}</div>
                          <div>{entry.parent_phone}</div>
                        </td>
                        <td className="text-zinc-500 text-xs">
                          {new Date(entry.created_at).toLocaleDateString()}
                        </td>
                        <td>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveFromWaitlist(entry.id)}
                            className="text-red-500 hover:text-red-400"
                            data-testid={`remove-waitlist-${entry.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          {/* Contact Messages Tab */}
          <TabsContent value="messages">
            <h2 className="font-heading text-2xl font-bold text-white mb-6">CONTACT MESSAGES</h2>
            
            {isLoading ? (
              <p className="text-zinc-400">Loading messages...</p>
            ) : contactMessages.length === 0 ? (
              <div className="text-center py-16 bg-zinc-950 border border-white/10 rounded-sm">
                <MessageSquare className="w-16 h-16 text-burns-gold mx-auto mb-4" />
                <p className="text-zinc-400">No messages yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {contactMessages.map(message => (
                  <div 
                    key={message.id} 
                    className="bg-zinc-950 border border-white/10 rounded-sm p-4 sm:p-6"
                    data-testid={`message-${message.id}`}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-3">
                      <div>
                        <h3 className="text-white font-medium text-lg">{message.name}</h3>
                        <p className="text-burns-gold text-sm">{message.email}</p>
                        {message.phone && (
                          <p className="text-zinc-500 text-sm">{message.phone}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-zinc-500 text-xs">
                          {new Date(message.created_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteContactMessage(message)}
                          className="text-zinc-500 hover:text-burns-red"
                          data-testid={`delete-message-${message.id}`}
                          aria-label="Delete message"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="bg-burns-dark rounded-sm p-4 border-l-2 border-burns-gold">
                      <p className="text-zinc-300 whitespace-pre-wrap">{message.message}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Newsletter Subscribers Tab */}
          <TabsContent value="newsletter">
            <h2 className="font-heading text-2xl font-bold text-white mb-6">NEWSLETTER SUBSCRIBERS</h2>
            
            {isLoading ? (
              <p className="text-zinc-400">Loading subscribers...</p>
            ) : newsletterSubscribers.length === 0 ? (
              <div className="text-center py-16 bg-zinc-950 border border-white/10 rounded-sm">
                <Mail className="w-16 h-16 text-burns-gold mx-auto mb-4" />
                <p className="text-zinc-400">No subscribers yet</p>
              </div>
            ) : (
              <>
                {/* Export hint */}
                <div className="bg-burns-dark border border-burns-gold/30 rounded-sm p-4 mb-6">
                  <p className="text-zinc-400 text-sm">
                    <span className="text-burns-gold font-medium">{newsletterSubscribers.length} subscribers</span> — 
                    Copy emails below to use in your email marketing tool.
                  </p>
                </div>

                <div className="bg-zinc-950 border border-white/10 rounded-sm overflow-hidden">
                  <table className="w-full admin-table text-sm">
                    <thead>
                      <tr className="bg-burns-dark">
                        <th>Email Address</th>
                        <th>Subscribed Date</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {newsletterSubscribers.map((subscriber, index) => (
                        <tr key={subscriber.id || index} data-testid={`subscriber-row-${index}`}>
                          <td className="text-burns-gold font-medium">{subscriber.email}</td>
                          <td className="text-zinc-400">
                            {new Date(subscriber.created_at).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </td>
                          <td>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteSubscriber(subscriber)}
                              className="text-zinc-500 hover:text-burns-red"
                              data-testid={`delete-subscriber-${subscriber.id || index}`}
                              aria-label="Remove subscriber"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Quick copy all emails */}
                <div className="mt-4">
                  <Button
                    onClick={() => {
                      const emails = newsletterSubscribers.map(s => s.email).join(', ');
                      navigator.clipboard.writeText(emails);
                      toast.success("All emails copied to clipboard!");
                    }}
                    className="btn-secondary"
                    data-testid="copy-emails-btn"
                  >
                    <Mail className="w-4 h-4 mr-2" /> COPY ALL EMAILS
                  </Button>
                </div>
              </>
            )}
          </TabsContent>

          {/* Camp Photos Tab */}
          <TabsContent value="photos">
            <CampPhotoManager />
          </TabsContent>

          {/* Parent Portal Stats Tab */}
          <TabsContent value="portal-stats">
            <ParentPortalStats />
          </TabsContent>

          {/* Email Tool Tab */}
          <TabsContent value="email-tool">
            <AdminEmailTool />
          </TabsContent>
        </Tabs>
      </main>

      <ConfirmDialog
        open={confirmState.open}
        onOpenChange={setConfirmOpen}
        title={confirmState.title}
        description={confirmState.description}
        confirmLabel={confirmState.confirmLabel}
        cancelLabel={confirmState.cancelLabel}
        destructive={confirmState.destructive}
        onConfirm={confirmState.onConfirm}
      />
    </div>
  );
}
